import { parseISO } from "date-fns";
import { z } from "zod";

const StripeConnectionSchema = z.object({
  environment: z.enum(["test", "live"]),
  status: z.enum(["not_connected", "setup_incomplete", "verification_pending", "connected", "action_required", "connection_removed"]),
  account: z.object({
    id: z.string(), businessName: z.string().nullable(), country: z.string(), currency: z.string().nullable(),
  }).nullable(),
  lastSyncedAt: z.date().nullable(),
});
const StripeConnectionDtoSchema = z.object({
  environment: StripeConnectionSchema.shape.environment,
  status: StripeConnectionSchema.shape.status,
  account: z.object({
    id: z.string(), business_name: z.string().nullable(), country: z.string(), currency: z.string().nullable(),
  }).nullable(),
  last_synced_at: z.iso.datetime({ offset: true }).nullable(),
});
export type StripeConnectionDto = z.infer<typeof StripeConnectionDtoSchema>;
export type StripeConnection = z.infer<typeof StripeConnectionSchema>;

export function fromStripeConnectionDtoToStripeConnection(dto: StripeConnectionDto): StripeConnection {
  const parsed = StripeConnectionDtoSchema.parse(dto);
  return {
    environment: parsed.environment, status: parsed.status,
    account: parsed.account ? {
      id: parsed.account.id, businessName: parsed.account.business_name,
      country: parsed.account.country, currency: parsed.account.currency,
    } : null,
    lastSyncedAt: parsed.last_synced_at ? parseISO(parsed.last_synced_at) : null,
  };
}
const StripeFinanceTotalsDtoSchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  gross_minor: z.number().int(),
  net_minor: z.number().int(),
  paid_out_minor: z.number().int(),
  payment_count: z.number().int().nonnegative(),
});
const StripeFinancePreviousTotalsDtoSchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  gross_minor: z.number().int(),
  net_minor: z.number().int(),
  payment_count: z.number().int().nonnegative(),
});
const StripeFinanceSummaryDtoSchema = z.object({
  environment: StripeConnectionSchema.shape.environment,
  status: StripeConnectionSchema.shape.status,
  period: z.object({
    start: z.iso.datetime({ offset: true }),
    end: z.iso.datetime({ offset: true }),
    timezone: z.string(),
  }),
  totals: z.array(StripeFinanceTotalsDtoSchema),
  previous_period: z.object({
    start: z.iso.datetime({ offset: true }),
    end: z.iso.datetime({ offset: true }),
    totals: z.array(StripeFinancePreviousTotalsDtoSchema),
  }).nullable().default(null),
  as_of: z.iso.datetime({ offset: true }),
});
export type StripeFinanceSummaryDto = z.infer<typeof StripeFinanceSummaryDtoSchema>;
export interface StripeFinanceTotals {
  currency: string;
  grossMinor: number;
  netMinor: number;
  paidOutMinor: number;
  paymentCount: number;
}
export interface StripeFinancePreviousTotals {
  currency: string;
  grossMinor: number;
  netMinor: number;
  paymentCount: number;
}
export interface StripeFinanceSummary {
  environment: StripeConnection["environment"];
  status: StripeConnection["status"];
  period: { start: Date; end: Date; timezone: string };
  totals: StripeFinanceTotals[];
  /** The same elapsed span of the previous month, when Stripe could summarize it. */
  previousPeriod: { start: Date; end: Date; totals: StripeFinancePreviousTotals[] } | null;
  asOf: Date;
}

export function fromStripeFinanceSummaryDtoToStripeFinanceSummary(dto: StripeFinanceSummaryDto): StripeFinanceSummary {
  const parsed = StripeFinanceSummaryDtoSchema.parse(dto);
  return {
    environment: parsed.environment, status: parsed.status,
    period: { start: parseISO(parsed.period.start), end: parseISO(parsed.period.end), timezone: parsed.period.timezone },
    totals: parsed.totals.map((total) => ({
      currency: total.currency, grossMinor: total.gross_minor, netMinor: total.net_minor, paidOutMinor: total.paid_out_minor,
      paymentCount: total.payment_count,
    })),
    previousPeriod: parsed.previous_period ? {
      start: parseISO(parsed.previous_period.start), end: parseISO(parsed.previous_period.end),
      totals: parsed.previous_period.totals.map((total) => ({
        currency: total.currency, grossMinor: total.gross_minor, netMinor: total.net_minor, paymentCount: total.payment_count,
      })),
    } : null,
    asOf: parseISO(parsed.as_of),
  };
}
